import React, { useEffect, useMemo, useState } from "react";
import { getKapasitetsOverstyringer } from "../../lib/tjenestekatalogApi";
import {
  normalizeWorkshopSlug,
  isoDate,
  weekStartISO,
  getWeekNumber,
  prettifyStoreName,
  tidsstyrtVerdi,
  ALL_STORES,
  DAGSMÅL_PER_BUTIKK,
  fmtKr,
  isDateClosed,
} from "./workshopCapacityUtils";
import WorkshopDagDetaljerModal from "./WorkshopDagDetaljerModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DAY_NAMES = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
const MONTH_NAMES = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

// Flatt månedsbudsjett inntil vi får et differensiert budsjett måned for måned.
const MÅNEDSBUDSJETT_TOTAL = 100000;

const BUTIKK_VEKT_SUM = ALL_STORES.reduce((s, slug) => s + (DAGSMÅL_PER_BUTIKK[slug] || 0), 0);

// Budsjettet fordeles mellom butikkene med samme vekting som de daglige
// omsetningsmålene (DAGSMÅL_PER_BUTIKK), inntil det finnes et eget
// månedsbudsjett pr. butikk.
function getMånedsbudsjettForStore(storeSlug) {
  if (storeSlug === "alle") return MÅNEDSBUDSJETT_TOTAL;
  if (!BUTIKK_VEKT_SUM) return 0;
  const vekt = DAGSMÅL_PER_BUTIKK[normalizeWorkshopSlug(storeSlug)] || 0;
  return Math.round((vekt / BUTIKK_VEKT_SUM) * MÅNEDSBUDSJETT_TOTAL);
}

function daysInMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

export default function WorkshopOmsetningMonthTab({ storeSlug }) {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  const [overrides, setOverrides] = useState([]);
  const [modal, setModal] = useState({ open: false, title: "", services: [] });

  const isAlle = storeSlug === "alle";

  const today = useMemo(() => new Date(), []);
  const monthDate = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1),
    [today, monthOffset]
  );
  const year = monthDate.getFullYear();
  const monthIndex0 = monthDate.getMonth();
  const isCurrentMonth = monthOffset === 0;

  const firstDayISO = useMemo(() => isoDate(new Date(year, monthIndex0, 1)), [year, monthIndex0]);
  const lastDayISO = useMemo(
    () => isoDate(new Date(year, monthIndex0, daysInMonth(year, monthIndex0))),
    [year, monthIndex0]
  );
  const todayISO = useMemo(() => isoDate(today), [today]);
  const cutoffISO = isCurrentMonth ? todayISO : lastDayISO;

  const monthDays = useMemo(() => {
    const total = daysInMonth(year, monthIndex0);
    const list = [];
    for (let day = 1; day <= total; day++) {
      const date = new Date(year, monthIndex0, day);
      const weekdayIdx = (date.getDay() + 6) % 7; // 0=Man … 6=Søn
      if (weekdayIdx === 6) continue; // søndag vises ikke
      list.push({
        iso: isoDate(date),
        date,
        weekIso: weekStartISO(date),
        weekNo: getWeekNumber(date),
        label: DAY_NAMES[weekdayIdx],
        isSaturday: weekdayIdx === 5,
      });
    }
    return list;
  }, [year, monthIndex0]);

  const virkedagerIMåned = useMemo(
    () => monthDays.filter((d) => !d.isSaturday).length,
    [monthDays]
  );

  const budsjett = getMånedsbudsjettForStore(storeSlug);
  const dagsmålSnitt = virkedagerIMåned > 0 ? budsjett / virkedagerIMåned : 0;

  const dagsmålPerButikk = useMemo(() => {
    const map = {};
    for (const slug of ALL_STORES) {
      map[slug] = virkedagerIMåned > 0 ? getMånedsbudsjettForStore(slug) / virkedagerIMåned : 0;
    }
    return map;
  }, [virkedagerIMåned]);

  useEffect(() => {
    getKapasitetsOverstyringer()
      .then((result) => setOverrides(result || []))
      .catch(() => setOverrides([]));
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const slugs = isAlle ? ALL_STORES : [normalizeWorkshopSlug(storeSlug)];

        const responses = await Promise.all(
          slugs.map((slug) =>
            fetch(
              `${API_BASE_URL}/workshop/capacity-history?verkstedSlug=${encodeURIComponent(slug)}&from=${firstDayISO}&to=${lastDayISO}`
            ).then((r) => (r.ok ? r.json() : { details: [] }))
          )
        );

        setDetails(responses.flatMap((r) => (r.details || []).filter((d) => !d.reason)));
      } catch (err) {
        console.error("Feil ved henting av månedsomsetning:", err);
        setDetails([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [storeSlug, isAlle, firstDayISO, lastDayISO]);

  // Dag-for-dag (man–lør) omsetning og akkumulert
  const allDayRows = useMemo(() => {
    let cum = 0;
    const rows = monthDays.map((wd) => {
      let isClosed = false;
      let dayDagsmål = 0;
      let missedMål = 0;

      if (!wd.isSaturday) {
        if (isAlle) {
          isClosed = ALL_STORES.every((slug) => isDateClosed(overrides, slug, wd.iso, wd.weekIso));
          for (const slug of ALL_STORES) {
            if (isDateClosed(overrides, slug, wd.iso, wd.weekIso)) {
              missedMål += dagsmålPerButikk[slug] || 0;
            } else {
              dayDagsmål += dagsmålPerButikk[slug] || 0;
            }
          }
        } else {
          const slug = normalizeWorkshopSlug(storeSlug);
          isClosed = isDateClosed(overrides, slug, wd.iso, wd.weekIso);
          dayDagsmål = isClosed ? 0 : dagsmålSnitt;
          missedMål = isClosed ? dagsmålSnitt : 0;
        }
      }

      const dayServices = details.filter((d) => d.ferdigDato === wd.iso);
      const dayArbeidskost = dayServices.reduce((s, d) => s + (d.omsetning || 0), 0);
      const dayTidsstyrtMin = dayServices.reduce((s, d) => s + (d.tidsstyrtMin || 0), 0);
      const dayTidsstyrtVerdi = tidsstyrtVerdi(dayTidsstyrtMin);
      const dayOms = dayArbeidskost + dayTidsstyrtVerdi;
      const antall = dayServices.length;
      const isPast = wd.iso <= cutoffISO;
      const isToday = isCurrentMonth && wd.iso === todayISO;
      if (isPast) cum += dayOms;
      const cumAtDay = isPast ? cum : null;
      const dagPct = dayDagsmål > 0 && isPast ? Math.min(200, Math.round((dayOms / dayDagsmål) * 100)) : null;

      return {
        ...wd,
        dayOms,
        dayArbeidskost,
        dayTidsstyrtVerdi,
        antall,
        cumAtDay,
        isPast,
        isToday,
        dagPct,
        isClosed,
        dayDagsmål,
        missedMål,
      };
    });
    return rows;
  }, [monthDays, details, cutoffISO, todayISO, isCurrentMonth, overrides, storeSlug, isAlle, dagsmålSnitt, dagsmålPerButikk]);

  // Lørdag vises i tabellen kun dersom det er servicer avsluttet den dagen
  const dayRows = useMemo(
    () => allDayRows.filter((row) => !row.isSaturday || row.antall > 0),
    [allDayRows]
  );

  // Ukedager (man–fre) — grunnlaget for "antall servicedager i måneden".
  // Lørdag holdes utenfor her siden den ikke inngår i det 5-dagers baserte
  // budsjettet (DAGSMÅL_PER_BUTIKK), selv om en eventuell lørdagsomsetning
  // fortsatt teller med i "Omsetning hittil".
  const weekdayRows = useMemo(
    () => allDayRows.filter((row) => !row.isSaturday),
    [allDayRows]
  );

  // Antall faktiske servicedager i måneden = ukedager minus stengte dager
  const totalServiceDaysInMonth = useMemo(
    () => weekdayRows.filter((d) => !d.isClosed).length,
    [weekdayRows]
  );
  const serviceDaysElapsed = useMemo(
    () => weekdayRows.filter((d) => d.isPast && !d.isClosed).length,
    [weekdayRows]
  );
  const serviceDaysRemaining = Math.max(0, totalServiceDaysInMonth - serviceDaysElapsed);
  const closedServiceDaysInMonth = useMemo(
    () => weekdayRows.filter((d) => d.isClosed).length,
    [weekdayRows]
  );

  const totalSoFar = dayRows.filter((d) => d.isPast).reduce((s, d) => s + d.dayOms, 0);
  const remaining = Math.max(0, budsjett - totalSoFar);
  const pctOfBudsjett = budsjett > 0 ? Math.min(100, Math.round((totalSoFar / budsjett) * 100)) : 0;
  const goalReached = budsjett > 0 && totalSoFar >= budsjett;

  // Projeksjon: når nås månedsbudsjettet basert på snittet så langt?
  // Snittet regnes pr. faktisk servicedag (ukedag som ikke er stengt) —
  // ikke bare dager med registrert omsetning, som tidligere ga et for
  // optimistisk snitt (stille dager ble "usynlige" i utregningen).
  const projeksjon = useMemo(() => {
    if (!budsjett || goalReached) return null;
    if (serviceDaysElapsed === 0 || totalSoFar === 0) return null;
    const avgPerDay = totalSoFar / serviceDaysElapsed;
    if (avgPerDay <= 0) return null;
    const daysNeeded = Math.ceil(remaining / avgPerDay);

    // Gå fremover dag for dag gjennom de faktiske gjenstående
    // servicedagene (hopper naturlig over søndager og stengte dager)
    // i stedet for å legge til N kalenderdager blindt.
    const upcomingOpenDays = weekdayRows.filter((d) => !d.isPast && !d.isClosed);
    if (daysNeeded > upcomingOpenDays.length) {
      return { projISO: null, projDate: null, projInThisMonth: false };
    }
    const projDate = upcomingOpenDays[daysNeeded - 1].date;
    return { projISO: isoDate(projDate), projDate, projInThisMonth: true };
  }, [budsjett, goalReached, serviceDaysElapsed, totalSoFar, remaining, weekdayRows]);

  const totalMissedMål = useMemo(
    () => dayRows.reduce((s, d) => s + (d.missedMål || 0), 0),
    [dayRows]
  );

  const totalTidsstyrtVerdi = useMemo(
    () => dayRows.filter((d) => d.isPast).reduce((s, d) => s + (d.dayTidsstyrtVerdi || 0), 0),
    [dayRows]
  );

  const totalArbeidskost = useMemo(
    () => dayRows.filter((d) => d.isPast).reduce((s, d) => s + (d.dayArbeidskost || 0), 0),
    [dayRows]
  );

  function openDayModal(row) {
    const services = details.filter((d) => d.ferdigDato === row.iso);
    setModal({
      open: true,
      title: `${row.label} ${row.iso} — ${fmtKr(row.dayOms)}`,
      services,
    });
  }

  function projeksjonLabel() {
    if (!budsjett) return "Ingen omsetningsmål satt";
    if (goalReached) return "Månedsbudsjettet er nådd!";
    if (!projeksjon) return serviceDaysElapsed === 0 ? "Ingen omsetning registrert ennå" : "Ikke nok data";
    if (projeksjon.projInThisMonth)
      return `Estimert nådd: ${projeksjon.projDate.getDate()}.${String(projeksjon.projDate.getMonth() + 1).padStart(2, "0")}`;
    return "Månedsbudsjettet nås ikke denne måneden";
  }

  if (loading) return <div className="py-10 text-center">Laster månedsomsetning…</div>;

  return (
    <div className="space-y-6">
      <WorkshopDagDetaljerModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        services={modal.services}
        showButikk={isAlle}
      />

      {/* Header med månedsnavigasjon */}
      <div className="rounded border bg-green-50 p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm text-gray-600">Månedsomsetning for</div>
            <div className="text-xl font-bold">
              {isAlle ? "Alle verksteder" : prettifyStoreName(storeSlug)}
            </div>
            <div className="text-sm text-gray-700 mt-1">
              {MONTH_NAMES[monthIndex0]} {year} ({firstDayISO} – {lastDayISO}) — dag for dag mot månedsbudsjett ({budsjett > 0 ? `${budsjett.toLocaleString("nb-NO")} kr` : "ikke satt"})
              {dagsmålSnitt > 0 && ` · ${Math.round(dagsmålSnitt).toLocaleString("nb-NO")} kr/dag snitt`}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setMonthOffset((o) => o - 1)}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm"
            >
              ← Forrige
            </button>
            {!isCurrentMonth && (
              <button
                onClick={() => setMonthOffset(0)}
                className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-green-700"
              >
                Denne måneden
              </button>
            )}
            <button
              onClick={() => setMonthOffset((o) => Math.min(0, o + 1))}
              disabled={isCurrentMonth}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Neste →
            </button>
          </div>
        </div>
      </div>

      {/* KPI-kort */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded border p-4 bg-white">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Månedsbudsjett</div>
          <div className="text-2xl font-bold mt-1">
            {budsjett > 0 ? `${budsjett.toLocaleString("nb-NO")} kr` : "—"}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {dagsmålSnitt > 0 ? `${Math.round(dagsmålSnitt).toLocaleString("nb-NO")} kr/dag snitt` : ""}
          </div>
        </div>
        <div className="rounded border p-4 bg-white">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Omsetning hittil</div>
          <div className={`text-2xl font-bold mt-1 ${goalReached ? "text-green-600" : ""}`}>
            {totalSoFar > 0 ? `${totalSoFar.toLocaleString("nb-NO")} kr` : "—"}
          </div>
          <div className="text-xs text-gray-400 mt-1">{pctOfBudsjett} % av budsjett</div>
          {(totalArbeidskost > 0 || totalTidsstyrtVerdi > 0) && (
            <div className="text-xs text-gray-500 mt-1">
              {fmtKr(totalArbeidskost)} arbeidskost
              {totalTidsstyrtVerdi > 0 && (
                <> · <span className="text-orange-600">{fmtKr(totalTidsstyrtVerdi)} tidsstyrt</span></>
              )}
            </div>
          )}
        </div>
        <div className="rounded border p-4 bg-white">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Gjenstår</div>
          <div className={`text-2xl font-bold mt-1 ${goalReached ? "text-green-600" : "text-amber-600"}`}>
            {goalReached ? "Ingen" : budsjett > 0 ? `${remaining.toLocaleString("nb-NO")} kr` : "—"}
          </div>
          <div className="text-xs text-gray-400 mt-1">til månedsbudsjett</div>
        </div>
        <div className={`rounded border p-4 ${goalReached ? "bg-green-50 border-green-200" : "bg-white"}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Fremdrift</div>
          <div className={`text-2xl font-bold mt-1 ${goalReached ? "text-green-700" : ""}`}>
            {budsjett > 0 ? `${pctOfBudsjett} %` : "—"}
          </div>
          <div className="text-xs text-gray-400 mt-1">{projeksjonLabel()}</div>
          <div className="text-xs text-gray-400 mt-1">
            {serviceDaysElapsed}/{totalServiceDaysInMonth} servicedager
          </div>
        </div>
      </div>

      {/* Fremdriftslinje */}
      {budsjett > 0 && (
        <div className="rounded border bg-white p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Fremdrift mot månedsbudsjett</span>
            <span className={`font-semibold ${goalReached ? "text-green-700" : "text-gray-700"}`}>
              {totalSoFar.toLocaleString("nb-NO")} / {budsjett.toLocaleString("nb-NO")} kr
            </span>
          </div>
          <div className="relative h-5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                goalReached ? "bg-green-500" : pctOfBudsjett >= 70 ? "bg-green-400" : pctOfBudsjett >= 40 ? "bg-amber-400" : "bg-red-400"
              }`}
              style={{ width: `${pctOfBudsjett}%` }}
            />
          </div>
          <div className={`text-xs font-medium ${goalReached ? "text-green-700" : projeksjon && !projeksjon.projInThisMonth ? "text-red-600" : "text-gray-600"}`}>
            {projeksjonLabel()}
          </div>
          <div className="text-xs text-gray-500">
            {totalServiceDaysInMonth} servicedager i {MONTH_NAMES[monthIndex0].toLowerCase()} ({serviceDaysElapsed} passert, {serviceDaysRemaining} gjenstår
            {closedServiceDaysInMonth > 0 ? `, ${closedServiceDaysInMonth} stengt` : ""})
          </div>
        </div>
      )}

      {/* Stengte dager — omsetningspåvirkning */}
      {totalMissedMål > 0 && dagsmålSnitt > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <span className="text-amber-500 text-base mt-0.5 shrink-0">⚠</span>
          <div>
            <div className="text-sm font-semibold text-amber-800">
              Stengte dager og redusert kapasitet påvirker budsjettet
            </div>
            <div className="text-sm text-amber-700 mt-0.5">
              Månedsbudsjettet er justert ned med{" "}
              <strong>{Math.round(totalMissedMål).toLocaleString("nb-NO")} kr</strong>{" "}
              pga stengte dager — fra{" "}
              {Math.round(budsjett + totalMissedMål).toLocaleString("nb-NO")} kr til{" "}
              {budsjett.toLocaleString("nb-NO")} kr.
            </div>
          </div>
        </div>
      )}

      {/* Dag-for-dag tabell */}
      <div className="overflow-x-auto">
        <table className="min-w-full border rounded bg-white text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Dag</th>
              <th className="px-4 py-2 text-left">Dato</th>
              <th className="px-4 py-2 text-left">Uke</th>
              <th className="px-4 py-2 text-right">Arbeidskost</th>
              <th className="px-4 py-2 text-right">Tidsstyrt</th>
              <th className="px-4 py-2 text-right">Totalt</th>
              <th className="px-4 py-2 text-right">Antall</th>
              <th className="px-4 py-2 text-right">Akkumulert</th>
              <th className="px-4 py-2 text-right">Mot mål</th>
              <th className="px-4 py-2 min-w-[160px]"></th>
            </tr>
          </thead>
          <tbody>
            {dayRows.map((row) => {
              const cumPct = budsjett > 0 && row.cumAtDay != null
                ? Math.min(100, Math.round((row.cumAtDay / budsjett) * 100))
                : null;
              const cumReached = row.cumAtDay != null && budsjett > 0 && row.cumAtDay >= budsjett;

              const clickable = row.isPast && row.antall > 0;

              return (
                <tr
                  key={row.iso}
                  onClick={clickable ? () => openDayModal(row) : undefined}
                  title={clickable ? "Klikk for servicedetaljer" : undefined}
                  className={`border-t ${clickable ? "cursor-pointer" : ""} ${
                    row.isClosed ? "bg-gray-50 text-gray-400"
                    : row.isToday ? "bg-green-50 font-semibold"
                    : !row.isPast ? "text-gray-400"
                    : "hover:bg-blue-50"
                  }`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {row.label}
                    {row.isToday && (
                      <span className="ml-2 text-xs font-normal text-green-700 bg-green-100 px-1.5 py-0.5 rounded">i dag</span>
                    )}
                    {row.isClosed && (
                      <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">Stengt</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{row.iso}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">Uke {row.weekNo}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {row.isPast ? fmtKr(row.dayArbeidskost) : "–"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-orange-700">
                    {row.isPast && row.dayTidsstyrtVerdi > 0 ? fmtKr(row.dayTidsstyrtVerdi) : "–"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {row.isPast ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className={`font-medium ${row.dagPct != null && row.dagPct >= 100 ? "text-green-700" : row.dagPct != null && row.dagPct < 70 && row.dayOms > 0 ? "text-amber-700" : ""}`}>
                          {fmtKr(row.dayOms)}
                        </span>
                        {row.missedMål > 0 && (
                          <span className="text-xs text-amber-600 font-medium">
                            −{Math.round(row.missedMål).toLocaleString("nb-NO")} kr mål
                          </span>
                        )}
                      </div>
                    ) : "–"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {row.isPast ? row.antall || "0" : "–"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-medium">
                    {row.cumAtDay != null ? `${row.cumAtDay.toLocaleString("nb-NO")} kr` : "–"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {cumPct != null ? (
                      <span className={`font-medium ${cumReached ? "text-green-700" : ""}`}>
                        {cumPct} %
                      </span>
                    ) : "–"}
                  </td>
                  <td className="px-4 py-3">
                    {cumPct != null && (
                      <div className="flex items-center gap-2">
                        <div className="relative h-3 flex-1 rounded-full bg-gray-100 overflow-hidden min-w-[100px]">
                          <div
                            className={`h-full rounded-full ${cumReached ? "bg-green-500" : cumPct >= 70 ? "bg-green-400" : cumPct >= 40 ? "bg-amber-400" : "bg-red-400"}`}
                            style={{ width: `${cumPct}%` }}
                          />
                        </div>
                        {dagsmålSnitt > 0 && row.dayOms > 0 && (
                          <span className={`text-xs w-14 text-right ${row.dagPct >= 100 ? "text-green-700" : row.dagPct < 70 ? "text-amber-600" : "text-gray-500"}`}>
                            {row.dagPct} % dag
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t bg-gray-50 font-semibold">
            <tr>
              <td colSpan={3} className="px-4 py-2">Totalt hittil</td>
              <td className="px-4 py-2 text-right">{fmtKr(totalArbeidskost)}</td>
              <td className="px-4 py-2 text-right text-orange-700">
                {totalTidsstyrtVerdi > 0 ? fmtKr(totalTidsstyrtVerdi) : "—"}
              </td>
              <td className="px-4 py-2 text-right">{fmtKr(totalSoFar)}</td>
              <td className="px-4 py-2 text-right">
                {dayRows.filter((d) => d.isPast).reduce((s, d) => s + d.antall, 0)}
              </td>
              <td className="px-4 py-2 text-right">{fmtKr(totalSoFar)}</td>
              <td className="px-4 py-2 text-right">
                {budsjett > 0 ? (
                  <span className={goalReached ? "text-green-700" : ""}>
                    {pctOfBudsjett} %
                  </span>
                ) : "—"}
              </td>
              <td className="px-4 py-2" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
